/**
 * The web overflow menu. The kit has no `Menu` component, so this is a
 * `Dialog` opened from URL state, exactly like
 * the pool actions sheet the pool home card stack used to carry. It lists whatever
 * `buildMemberOverflowMenu` returned.
 *
 * `readyActionIds` is the caller's honest account of which ids it can act on
 * right now. A row whose id is not in that set renders disabled, with
 * `MEMBER_ACTIONS_NOT_READY_NOTE` attached directly under it, so a fan never
 * sees a control that answers a press with nothing. ISSUE-775.
 *
 * `viewProfile` became ready on 2026-09-05 (ISSUE-194), ISSUE-817 wired the
 * five role and membership ids, and ISSUE-1045 wired `message` and `report`,
 * so the pool members pane now passes every id as ready and no row renders the
 * note. The `readyActionIds` mechanism is kept until a refactor card removes
 * it.
 *
 * `busyActionId` names the one row whose request is open, which goes busy and
 * cannot be pressed again until it settles (ISSUE-775).
 */
import { Button, Dialog } from "@sportsmet/ui";
import { type ReactNode } from "react";

import type {
  typeMemberMenuActionId,
  typeMemberMenuItem,
} from "./memberOverflowMenu";

export const MEMBER_ACTIONS_TITLE = "Member actions";
export const MEMBER_ACTIONS_NOT_READY_NOTE =
  "Not ready yet. We're still building this. Check back soon.";

export type typeMemberActionsDialogProps = {
  open: boolean;
  memberName: string;
  items: readonly typeMemberMenuItem[];
  /** ids the caller can act on right now. Anything else renders disabled. */
  readyActionIds: ReadonlySet<typeMemberMenuActionId>;
  /**
   * The one action whose request is in flight right now, or `null`. That row
   * renders busy and cannot be pressed again until the request settles.
   * Readiness and busy-ness are different things: a busy row keeps the ready
   * treatment and never gains the not-ready note.
   */
  busyActionId?: typeMemberMenuActionId | null;
  onOpenChange: (open: boolean) => void;
  /** Only ever called for a ready row; a disabled row cannot be pressed. */
  onSelect: (id: typeMemberMenuActionId) => void;
};

export const MemberActionsDialog = ({
  open,
  memberName,
  items,
  readyActionIds,
  busyActionId = null,
  onOpenChange,
  onSelect,
}: typeMemberActionsDialogProps): ReactNode => {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={MEMBER_ACTIONS_TITLE}
      description={memberName}
      autoFocus={false}
    >
      <div className="flex flex-col gap-2">
        {items.map((item) => {
          const isReady = readyActionIds.has(item.id);
          const isBusy = item.id === busyActionId;
          const noteId = `member-action-${item.id}-note`;
          return (
            <div key={item.id} className="flex flex-col gap-1">
              <Button
                fullWidth
                size="md"
                variant={item.destructive ? "destructive" : "secondary"}
                data-testid={`member-action-${item.id}`}
                disabled={!isReady}
                loading={isBusy}
                aria-describedby={isReady ? undefined : noteId}
                onClick={() => {
                  onSelect(item.id);
                }}
              >
                {item.label}
              </Button>
              {isReady ? null : (
                <p
                  id={noteId}
                  className="text-caption text-ink-secondary"
                  data-testid={noteId}
                >
                  {MEMBER_ACTIONS_NOT_READY_NOTE}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </Dialog>
  );
};
